import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import logo from '../../assets/images/logo.svg';

const navLinks = [
  { path: "/", display: "Home" },
  { path: "/events", display: "Events" },
  { path: "/booking", display: "Booking" },
  { path: "/tours", display: "Tours" },
  { path: "/services", display: "Services" },
  { path: "/contact", display: "Contact" },
];

const Header = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isMobileMenuOpen]);

  return (
    <>
      <style>{`
        :root {
          --primary: #0D9488;
          --primary-dark: #0F766E;
          --accent: #8B5CF6;
          --amber: #F59E0B;
          --bg-light: #ffffff;
          --text-light: #0f172a;
          --text-muted-light: #475569;
          --bg-dark: #020817;
          --text-dark: #f8fafc;
          --text-muted-dark: #94a3b8;
          --glass-bg-light: rgba(255, 255, 255, 0.75);
          --glass-border-light: rgba(13, 148, 136, 0.08);
          --glass-bg-dark: rgba(2, 8, 23, 0.85);
          --glass-border-dark: rgba(13, 148, 136, 0.15);
        }

        .oscar-header * { box-sizing: border-box; margin: 0; padding: 0; }
        
        .oscar-header {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1000;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          padding: 1.5rem 0;
        }

        .header-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 2rem;
        }

        .oscar-header.scrolled {
          padding: 1rem 0;
          background: var(--glass-bg-light);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-bottom: 1px solid var(--glass-border-light);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
        }

        .oscar-header.dark.scrolled {
          background: var(--glass-bg-dark);
          border-bottom: 1px solid var(--glass-border-dark);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
        }

        .logo-wrapper {
          cursor: pointer;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          align-items: center;
        }

        .logo-wrapper:hover { transform: scale(1.03); }
        .logo-wrapper img { height: 36px; width: auto; object-fit: contain; }

        .desktop-nav { display: flex; align-items: center; gap: 2.5rem; }

        .nav-link {
          position: relative;
          font-weight: 500;
          font-size: 0.95rem;
          letter-spacing: 0.01em;
          color: var(--text-muted-light);
          text-decoration: none;
          transition: color 0.3s ease;
          padding: 0.5rem 0;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, var(--primary), var(--accent));
          transform: scaleX(0);
          transform-origin: right;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          border-radius: 2px;
        }

        .nav-link:hover, .nav-link.active { color: var(--primary); }
        .nav-link:hover::after, .nav-link.active::after { transform: scaleX(1); transform-origin: left; }

        .dark .nav-link { color: var(--text-muted-dark); }
        .dark .nav-link:hover, .dark .nav-link.active { color: #a7f3d0; }

        .auth-wrapper { display: flex; align-items: center; gap: 1rem; }

        .btn-auth {
          padding: 0.65rem 1.6rem;
          border-radius: 100px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          white-space: nowrap;
        }

        .btn-login {
          background: transparent;
          color: var(--text-light);
          border: 1px solid rgba(15, 23, 42, 0.12);
        }
        .btn-login:hover {
          background: rgba(13, 148, 136, 0.06);
          border-color: var(--primary);
          color: var(--primary);
        }

        .btn-register {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          color: #fff;
          box-shadow: 0 4px 14px rgba(13, 148, 136, 0.25);
        }
        .btn-register:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(13, 148, 136, 0.4);
        }

        .dark .btn-login { color: #e2e8f0; border-color: rgba(255, 255, 255, 0.12); }
        .dark .btn-login:hover {
          background: rgba(13, 148, 136, 0.15);
          border-color: #a7f3d0;
          color: #a7f3d0;
        }

        .mobile-toggle {
          display: none;
          background: transparent;
          border: none;
          color: var(--text-light);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: background 0.2s;
        }
        .mobile-toggle:hover { background: rgba(13, 148, 136, 0.08); }
        .dark .mobile-toggle { color: var(--text-dark); }
        .dark .mobile-toggle:hover { background: rgba(13, 148, 136, 0.15); }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 1001;
          opacity: 0;
          visibility: hidden;
          transition: all 0.4s ease;
        }
        .overlay.open { opacity: 1; visibility: visible; }

        .mobile-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 100%;
          max-width: 380px;
          height: 100vh;
          background: var(--bg-light);
          z-index: 1002;
          transform: translateX(100%);
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: -10px 0 40px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          padding: 2rem;
        }
        .mobile-drawer.open { transform: translateX(0); }

        .dark .mobile-drawer {
          background: #0f172a;
          box-shadow: -10px 0 40px rgba(0,0,0,0.5);
        }

        .drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 3rem;
        }
        .drawer-header img { height: 32px; }
        .drawer-header button {
          background: transparent;
          border: none;
          color: var(--text-muted-light);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: background 0.2s;
        }
        .drawer-header button:hover { background: rgba(13, 148, 136, 0.08); }
        .dark .drawer-header button { color: var(--text-muted-dark); }
        .dark .drawer-header button:hover { background: rgba(13, 148, 136, 0.15); }

        .mobile-nav { display: flex; flex-direction: column; gap: 0.5rem; flex: 1; }

        .mobile-nav-link {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-light);
          text-decoration: none;
          padding: 1rem 0;
          border-bottom: 1px solid rgba(15, 23, 42, 0.06);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .mobile-nav-link:hover { color: var(--primary); padding-left: 0.5rem; }
        .mobile-nav-link.active { color: var(--primary); }

        .dark .mobile-nav-link {
          color: var(--text-dark);
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }
        .dark .mobile-nav-link:hover, .dark .mobile-nav-link.active { color: #a7f3d0; }

        .drawer-auth { display: flex; flex-direction: column; gap: 1rem; margin-top: 2rem; }
        .drawer-auth .btn-auth { width: 100%; padding: 1rem; font-size: 1rem; }

        @media (max-width: 1024px) {
          .desktop-nav, .desktop-auth { display: none; }
          .mobile-toggle { display: flex; }
          .header-container { padding: 0 1.5rem; }
        }

        @media (max-width: 480px) {
          .mobile-drawer { max-width: 100%; }
        }
      `}</style>

      <header className={`oscar-header ${isScrolled ? 'scrolled' : ''} ${isDarkMode ? 'dark' : ''}`}>
        <div className="header-container">
          <div className="logo-wrapper" onClick={() => navigate('/')}>
            <img src={logo} alt="Oscar Global" />
          </div>
          
          <nav className="desktop-nav">
            {navLinks.map(link => (
              <NavLink 
                key={link.path} 
                to={link.path} 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                {link.display}
              </NavLink>
            ))}
          </nav>

          <div className="auth-wrapper desktop-auth">
            <Link to="/login" className="btn-auth btn-login">Login</Link>
            <Link to="/register" className="btn-auth btn-register">Get Started</Link>
          </div>

          <button 
            className="mobile-toggle" 
            onClick={() => setIsMobileMenuOpen(true)} 
            aria-label="Open navigation menu"
          >
            <Menu size={24} strokeWidth={2} />
          </button>
        </div>
      </header>

      {/* Mobile Overlay & Drawer */}
      <div 
        className={`overlay ${isMobileMenuOpen ? 'open' : ''}`} 
        onClick={() => setIsMobileMenuOpen(false)} 
      />
      
      <div className={`mobile-drawer ${isDarkMode ? 'dark' : ''} ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <img src={logo} alt="Oscar Global" />
          <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Close navigation menu">
            <X size={24} strokeWidth={2} />
          </button>
        </div>
        
        <nav className="mobile-nav">
          {navLinks.map(link => (
            <NavLink 
              key={link.path} 
              to={link.path} 
              className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.display}
            </NavLink>
          ))}
        </nav>
        
        <div className="drawer-auth">
          <Link to="/login" className="btn-auth btn-login" onClick={() => setIsMobileMenuOpen(false)}>Login</Link>
          <Link to="/register" className="btn-auth btn-register" onClick={() => setIsMobileMenuOpen(false)}>Get Started</Link>
        </div>
      </div>
    </>
  );
};

export default Header;